import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Loader2, Globe2, Clock, AudioLines } from "lucide-react";
import { formatTime } from "./TranscriptView";

export default function SharedTranscript() {
    const { id } = useParams();
    const [t, setT] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get(`/share/${id}`);
                setT(res.data);
            } catch (e) {
                setErr(e?.response?.data?.detail || "Not found or sharing disabled");
            }
        })();
    }, [id]);

    return (
        <div className="min-h-screen bg-white">
            <header className="glass-header sticky top-0 z-50 border-b border-slate-200/60">
                <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center">
                            <AudioLines className="w-4 h-4 text-white" strokeWidth={2.5} />
                        </div>
                        <span className="text-lg font-semibold tracking-tight text-slate-900">Transcribely</span>
                    </Link>
                    <Link
                        to="/auth"
                        className="inline-flex items-center justify-center rounded-xl bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 transition-colors"
                        data-testid="shared-signup-cta"
                    >
                        Try free
                    </Link>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-6 py-10 sm:py-14">
                {err ? (
                    <div className="text-center py-20 text-slate-500" data-testid="share-error">{err}</div>
                ) : !t ? (
                    <div className="flex items-center justify-center py-32 text-slate-400">
                        <Loader2 className="w-6 h-6 animate-spin" />
                    </div>
                ) : (
                    <>
                        <p className="text-xs font-medium text-slate-400 uppercase tracking-widest">Shared transcript</p>
                        <h1 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 break-all">{t.source_name}</h1>
                        <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-slate-500">
                            {t.language && (
                                <span className="inline-flex items-center gap-1.5"><Globe2 className="w-3.5 h-3.5" strokeWidth={1.75} />{t.language}</span>
                            )}
                            {t.duration_seconds > 0 && (
                                <span className="inline-flex items-center gap-1.5 font-mono"><Clock className="w-3.5 h-3.5" strokeWidth={1.75} />{formatTime(t.duration_seconds)}</span>
                            )}
                        </div>

                        <div className="mt-10 space-y-1" data-testid="shared-body">
                            {(t.segments || []).map((s, i) => (
                                <div key={i} className="flex gap-4 p-4 rounded-xl hover:bg-slate-50 transition-colors group">
                                    <div className="shrink-0 w-24 pt-0.5">
                                        <div className="text-sm font-medium text-indigo-600 truncate">{prettySpeaker(s.speaker)}</div>
                                        <div className="text-xs font-mono text-slate-400 mt-0.5">{formatTime(s.start)}</div>
                                    </div>
                                    <p className="flex-1 text-[15px] leading-relaxed text-slate-800">{s.text}</p>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </main>
        </div>
    );
}

function prettySpeaker(s) {
    if (!s) return "Speaker";
    const m = s.match(/(\d+)/);
    if (m) return `Speaker ${parseInt(m[1], 10) + 1}`;
    return s;
}
